/** BADGE ACTIONS on a player, for staff.
 *
 *  The same four actions as the Players box — add a badge, remove one, unlock
 *  tonight's venue, release the phone — shown inside the player panel that
 *  Live Game View and Game Results already open. A venue's own manager may
 *  only act on their own venue's players; the server decides that, not this.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { Button } from '../ui/button';

export const PlayerBadgeActions = ({ organizationId, email, prefix = 'pb' }) => {
    const { t } = useTranslation();
    const [player, setPlayer] = useState(null);
    const [venues, setVenues] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        if (!organizationId || !email) return;
        try {
            const [found, box] = await Promise.all([
                api.get(`/goal-game/admin/players/find?organization_id=${organizationId}&q=${encodeURIComponent(email)}`),
                api.get(`/goal-game/admin/players?organization_id=${organizationId}`),
            ]);
            setPlayer(found.data.player);
            setVenues(box.data.venues || []);
        } catch (_e) { setPlayer(null); }
    }, [organizationId, email]);

    useEffect(() => { load(); }, [load]);

    const act = async (body) => {
        setBusy(true);
        try {
            const res = await api.post('/goal-game/admin/players/edit',
                                       { organization_id: organizationId, email, ...body });
            setPlayer(res.data.player);
        } catch (_e) { /* the server refused: nothing changes on screen */ }
        finally { setBusy(false); }
    };

    if (!player) return null;

    return (
        <div className="space-y-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3"
             data-testid={`${prefix}-badges`}>
            <p className="text-sm">
                <span className="text-muted-foreground">{t('goalGame.badgesLabel')}: </span>
                {(player.badges || []).length === 0 ? '—' : player.badges.map((b) => (
                    <span key={b.venue_id} className="mr-2 inline-flex items-center gap-1">
                        {b.venue_name} ({b.how === 'link' ? t('goalGame.howLink') : t('goalGame.howCard')})
                        <button type="button" disabled={busy} className="text-xs text-red-600 hover:underline"
                                data-testid={`${prefix}-badge-remove-${b.venue_id}`}
                                onClick={() => act({ venue_id: b.venue_id, action: 'remove' })}>
                            {t('goalGame.removeBadge')}
                        </button>
                    </span>
                ))}
            </p>
            <div className="flex flex-wrap items-center gap-2">
                {venues.filter((v) => !(player.badges || []).some((b) => b.venue_id === v.id)).map((v) => (
                    <Button key={v.id} size="sm" variant="outline" disabled={busy}
                            data-testid={`${prefix}-badge-add-${v.id}`}
                            onClick={() => act({ venue_id: v.id, action: 'add' })}>
                        {t('goalGame.addBadge')}: {v.name}
                    </Button>
                ))}
            </div>
            <p className="text-sm">
                <span className="text-muted-foreground">{t('goalGame.playingTonight')}: </span>
                {player.tonight?.venue_name || '—'}
                {player.tonight && (
                    <Button size="sm" variant="outline" className="ml-2" disabled={busy}
                            data-testid={`${prefix}-badge-unlock`}
                            onClick={() => act({ action: 'unlock' })}>
                        {t('goalGame.unlockVenue')}
                    </Button>
                )}
            </p>
            <p className="text-sm">
                <span className="text-muted-foreground">{t('goalGame.phoneBound')}: </span>
                {player.phone_bound ? 'yes' : 'no'}
                {player.phone_bound && (
                    <Button size="sm" variant="outline" className="ml-2" disabled={busy}
                            data-testid={`${prefix}-badge-release`}
                            onClick={() => act({ action: 'release' })}>
                        {t('goalGame.releasePhone')}
                    </Button>
                )}
            </p>
        </div>
    );
};

export default PlayerBadgeActions;
